import api from './api';

export interface Meeting {
  id: number;
  title: string;
  scheduled_at: string;
  duration_minutes: number;
  session_type: string | null;
  notes: string | null;
  fee: number | null;
  status: 'active' | 'noshow' | 'cancelled' | 'done';
  patient_id: number;
  therapist_id: number;
  tenant_id: number;
  patient: {
    id: number;
    full_name: string;
    email: string | null;
  };
  therapist: {
    id: number;
    full_name: string;
    email: string;
  };
  created_at: string;
  updated_at: string;
}

export interface MeetingCreate {
  title: string;
  scheduled_at: string;
  duration_minutes?: number;
  session_type?: string;
  notes?: string;
  fee?: number;
  patient_id: number;
  therapist_id: number;
  tenant_id: number;
}

export interface MeetingUpdate {
  title?: string;
  scheduled_at?: string;
  duration_minutes?: number;
  session_type?: string;
  notes?: string;
  fee?: number;
  status?: 'active' | 'noshow' | 'cancelled' | 'done';
}

export const meetingService = {
  async getMeetings(
    tenantId: number,
    patientId?: number,
    therapistId?: number,
    status?: 'active' | 'noshow' | 'cancelled' | 'done' | 'all',
    dateRange: string = 'today'
  ): Promise<Meeting[]> {
    const params = new URLSearchParams({ tenant_id: tenantId.toString() });
    if (patientId) params.append('patient_id', patientId.toString());
    if (therapistId) params.append('therapist_id', therapistId.toString());
    if (status && status !== 'all') params.append('status', status);
    params.append('date_range', dateRange);

    const response = await api.get(`/meetings/?${params}`);
    return response.data;
  },

  async getMeetingsByDateRange(
    tenantId: number,
    fromDate: number,
    toDate: number,
    meetingTypeId: number = 0,
    userId?: number,
    onlyMy: boolean = false
  ): Promise<Meeting[]> {
    const params = new URLSearchParams({
      tenant_id: tenantId.toString(),
      from_date: fromDate.toString(),
      to_date: toDate.toString(),
      meeting_type_id: meetingTypeId.toString(),
      only_my: onlyMy.toString()
    });
    if (userId) params.append('user_id', userId.toString());

    const response = await api.get(`/meetings/meetings_by_dates?${params}`);
    return response.data;
  },

  async getMeeting(meetingId: number): Promise<Meeting> {
    const response = await api.get(`/meetings/${meetingId}`);
    return response.data;
  },

  async createMeeting(meetingData: MeetingCreate): Promise<Meeting> {
    const response = await api.post('/meetings/', meetingData);
    return response.data;
  },

  async updateMeeting(meetingId: number, meetingData: MeetingUpdate): Promise<Meeting> {
    const response = await api.put(`/meetings/${meetingId}`, meetingData);
    return response.data;
  },

  async updateMeetingStatus(
    meetingId: number,
    status: 'active' | 'noshow' | 'cancelled' | 'done'
  ): Promise<void> {
    await api.post(`/meetings/${meetingId}/status/${status}`);
  },

  async summarizeMeeting(meetingId: number): Promise<{
    message: string;
    job_id: number;
    status: string;
  }> {
    const response = await api.post(`/meetings/${meetingId}/summarize`);
    return response.data;
  },
};